import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articleContent } from "./content";
import { mainSiteUrl } from "@/data/categories";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];
  const content = (articleContent[lang] && articleContent[lang][slug || ""]) || articleContent.en[slug || ""];

  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!content) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
            <i className="ri-file-search-line text-2xl"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {t("article.notFound")}
          </h1>
          <p className="mt-3 text-foreground-600">{t("article.notFoundDesc")}</p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            {t("footer.home")}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: content.title,
    description: content.metaDescription,
    image: content.image,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/article/${slug}`,
    },
    author: { "@type": "Organization", name: content.author },
    publisher: {
      "@type": "Organization",
      name: "Basetao Spreadsheet",
      logo: {
        "@type": "ImageObject",
        url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png",
      },
    },
    dateModified: content.lastUpdated,
    inLanguage: lang,
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

      <Navbar />

      <main className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6">
        {/* Breadcrumb — internal link back to home */}
        <nav aria-label="Breadcrumb" className="mb-8 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="inline-flex items-center gap-1.5 transition hover:text-foreground-950 cursor-pointer">
            <i className="ri-home-4-line"></i>
            {t("footer.home")}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">{content.eyebrow}</span>
        </nav>

        <article>
          <header className="mb-10">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-900">
              <i className="ri-book-open-line"></i>
              {content.eyebrow}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {content.title}
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-foreground-600">{content.intro}</p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {content.author}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {content.lastUpdated}
              </span>
            </div>
          </header>

          <div className="mb-10 overflow-hidden rounded-lg border border-background-200/70">
            <img
              src={content.image}
              alt={content.title}
              title={`${content.title} - Basetao Spreadsheet`}
              className="h-56 w-full object-cover object-top md:h-72"
            />
          </div>

          <div className="flex flex-col gap-10">
            {content.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{section.heading}</h2>
                <div className="mt-4 flex flex-col gap-4">
                  {section.paragraphs.map((p) => (
                    <p key={p.slice(0, 20)} className="leading-relaxed text-foreground-700">
                      {p}
                    </p>
                  ))}
                </div>
              </section>
            ))}

            {/* Comparison table */}
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{content.comparisonTitle}</h2>
              <div className="mt-4 overflow-x-auto rounded-lg border border-background-200/70">
                <table className="w-full min-w-[560px] border-collapse bg-background-50 text-left text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      {content.comparisonHeaders.map((h) => (
                        <th key={h} className="px-4 py-3 font-heading text-sm font-semibold text-foreground-900">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {content.comparisonRows.map((row) => (
                      <tr key={row[0]} className="border-t border-background-200/70">
                        {row.map((cell, i) => (
                          <td
                            key={cell}
                            className={`px-4 py-3 ${i === 0 ? "font-semibold text-foreground-900" : "text-foreground-600"}`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* FAQ */}
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{content.faqTitle}</h2>
              <div className="mt-4 flex flex-col gap-3">
                {content.faqs.map((faq, index) => {
                  const isOpen = openFaq === index;
                  return (
                    <div key={faq.q} className="overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : index)}
                        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                      >
                        <span className="font-heading text-sm font-semibold text-foreground-900">{faq.q}</span>
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-transform ${
                            isOpen ? "rotate-45 bg-primary-100 text-primary-700" : ""
                          }`}
                        >
                          <i className="ri-add-line text-base"></i>
                        </span>
                      </button>
                      {isOpen && (
                        <div className="border-t border-background-200/70 px-5 py-4">
                          <p className="text-sm leading-relaxed text-foreground-600">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* CTA + internal link back home */}
            <section className="rounded-lg border border-primary-200/70 bg-primary-50 px-6 py-10 text-center">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{t("cta.title")}</h2>
              <p className="mx-auto mt-3 max-w-xl text-foreground-600">{t("cta.subtitle")}</p>
              <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link
                  to="/"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-primary-300 bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition hover:bg-primary-100 cursor-pointer whitespace-nowrap sm:w-auto"
                >
                  <i className="ri-home-4-line"></i>
                  {t("footer.home")}
                </Link>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600 cursor-pointer whitespace-nowrap sm:w-auto"
                >
                  {t("nav.visitMain")}
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </section>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}